import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { BREAKPOINTS } from "../../helpers/theme";
import {
  toVWDesktop,
  toVWMobile,
} from "../../helpers/styles";
import closeIcon from "../../assets/images/close_icon.svg";

export const VideoModal = ({
  src,
  onClose,
}: {
  src: string;
  onClose: () => void;
}): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  const getFocusableElements = () => {
    if (!containerRef.current) return [];
    const elements = containerRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    return Array.from(elements) as HTMLElement[];
  };

  useEffect(() => {
    const previouslyFocused = document.activeElement;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      } else if (event.key === "Tab") {
        const focusableElements = getFocusableElements();

        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement =
          focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            event.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            event.preventDefault();
          }
        }
      }
    };

    document.documentElement.style.overflow = "hidden";
    const ac = new AbortController();
    window.addEventListener("keydown", handleKeyDown, {
      signal: ac.signal,
    });

    closeButtonRef?.current?.focus();

    return () => {
      document.documentElement.style.removeProperty(
        "overflow",
      );
      ac.abort();

      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused?.focus();
      }
    };
  }, [onClose, iframeLoaded]);

  return (
    <Background
      onClick={onClose}
      aria-modal="true"
      role="dialog"
    >
      <VideoContainer
        ref={containerRef}
        onClick={(e) => e.stopPropagation()}
      >
        <CloseButton ref={closeButtonRef} onClick={onClose}>
          <img src={closeIcon} alt="close" />
        </CloseButton>
        <VideoIframe
          ref={iframeRef}
          src={src}
          title="Video content"
          onLoad={() => setIframeLoaded(true)}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </VideoContainer>
    </Background>
  );
};

const Background = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  padding-inline: 0;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    padding-inline: ${toVWDesktop(100)}vw;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  max-width: min(1280px, calc(65vh * (16 / 9)));
  max-height: 65vh;
`;

const CloseButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  border-radius: 50%;
  background-color: var(--color-white);
  top: ${toVWMobile(-10)}vw;
  right: ${toVWMobile(10)}vw;
  cursor: pointer;
  border: none;
  outline: none;
  width: ${toVWMobile(32)}vw;
  height: ${toVWMobile(32)}vw;
  padding: ${toVWMobile(8)}vw;
  transform: translateY(-100%);

  &:hover,
  &:focus-visible {
    opacity: 0.8;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    transform: translate(100%, -100%);
    width: ${toVWDesktop(40)}vw;
    height: ${toVWDesktop(40)}vw;
    top: ${toVWDesktop(-20)}vw;
    right: ${toVWDesktop(-20)}vw;
    padding: ${toVWDesktop(12)}vw;
  }
`;

const VideoIframe = styled.iframe`
  width: 100%;
  height: 100%;
  outline: none;
  border: none;
`;
